import wam from '@tcc/wam';
import envDetect from '@tcc/env-detect';
import queryString from 'query-string';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { scriptTypes } from 'constants/scripts';
import { farmerNavigationDefaultOperation } from 'constants/operations';

import { routeParse } from 'util/routes';
import { asciibeticalCompare } from 'util/sort';
import { extractFieldIdentifier } from 'fields/utils';
import { queryParam, getSearchParamOnPluginURL } from 'util/queryString';

import { getActiveProductYear, getActiveScriptType } from 'current/selectors';
import {
  getProductYearsForScripts,
  getScriptsSortedForManageScriptList,
  getScriptTypeForScript
} from 'scripts/selectors';
import { getApplicationScriptsSortedForManageScriptList } from 'scripts/application-selectors';
import { getFertilityScriptsSortedForManageScriptList } from 'scripts/fertility-selectors';
import { getProductYearByScriptType } from 'settings/selectors';
import { isPluginMode } from './isPluginMode';

const defaultQuery = (location && location.search) || '';
const defaultHash = (location && location.hash) || '';
const defaultReferrer = (document && document.referrer) || '';

export const determineEnv = () => {
  return envDetect.getCurrentEnv() || envDetect.environments.LOCAL;
};

export const isProdLikeEnv = () => {
  const env = determineEnv();
  switch (env) {
    case envDetect.environments.LOCAL:
      return false;
    case envDetect.environments.PRODUCTION:
      return true;
    default:
      return true;
  }
};

export const determineApiPrefix = ({
  requiresOldApiDomain,
  requiresFieldViewDomain
}) => {
  const domains = {
    [envDetect.environments.LOCAL]: '//local.climate.com',
    [envDetect.environments.QA1]: '//qa1.climate.com',
    [envDetect.environments.PRODUCTION]: '//api.climate.com'
  };
  if (requiresOldApiDomain) {
    domains[envDetect.environments.LOCAL] = '//local.climate.com';
    domains[envDetect.environments.QA1] = '//qa1.climate.com';
    domains[envDetect.environments.PRODUCTION] = '//climate.com';
  }
  if (requiresFieldViewDomain) {
    domains[envDetect.environments.LOCAL] = '//spatial.fieldviewplus.com';
    domains[envDetect.environments.PRODUCTION] = '//spatial.fieldviewplus.com';
  }
  return domains[determineEnv()] || '//climate.com';
};

export const determineAppMode = ({ query = defaultQuery } = {}) => {
  let params = queryString.parse(query);
  return params.APP_MODE === 'on' || params.APP_MODE === 'true';
};

export const determineAppVersion = ({ query = defaultQuery } = {}) => {
  // App version set by iOS app, and is used by Nitrogen Monitor and potentially other web sub-apps.
  let params = queryString.parse(query);
  return params.APP || '';
};

export const determineRouteVersion = ({ hash = defaultHash } = {}) => {
  return routeParse(hash).version;
};

export const determineCameFromSubfieldN = ({
  referrer = defaultReferrer
} = {}) => {
  return (/nitrogen-monitoring/).test(referrer);
};

export const determineDefaultScriptType = ({
  query = defaultQuery,
  referrer = defaultReferrer
} = {}) => {
  let scriptType = get(
    queryString.parse(query.toLowerCase()),
    'script_type',
    ''
  ).toLowerCase();
  let appMode = determineAppMode({ query });
  let cameFromSubfieldN = determineCameFromSubfieldN({ referrer });
  // sanity check to make sure ios or subfield n is sending what we expect
  if (
    (appMode || cameFromSubfieldN)
    && scriptType
    && Object.values(scriptTypes).includes(scriptType)
  ) {
    return scriptType;
  }

  // use fertility as default -- ios is going to send SCRIPT_TYPE either way,
  // and if that's not there, we want fertility as ultimate default
  return scriptTypes.FERTILITY;
};

export const determineUserId = ({
  query = defaultQuery,
  hash = defaultHash
} = {}) => {
  // first use a client-id from the query, if there is one
  let params = queryString.parse(query);
  if (params['client-id'] && params['client-id'] !== 'null') {
    return params['client-id'];
  }

  if(isPluginMode()) {
    const clientId = getSearchParamOnPluginURL('client-id');
    if(clientId) {
      return clientId;
    }
  }

  // then check to see if we have a route-path
  params = routeParse(hash);
  if (params.clientId && params.clientId !== 'null') {
    return params.clientId;
  }
  // otherwise use logged-in user
  return wam.getUserId();
};

export const determineTestCaseId = ({ query = defaultQuery }) => {
  let params = queryString.parse(query);
  return params.TEST_CASE_ID;
};

export const determineStartingView = ({ query = defaultQuery }) => {
  let params = queryString.parse(query);
  return params['starting-view'];
};

export const determineActualUserId = () => {
  // The person logged in as, not the person being proxied as
  return wam.getUserId();
};

const convertUnknownIdentifier = ({ identifier }) => {
  return identifier
    ? {
      id: identifier.includes('-') ? null : identifier,
      uuid: identifier.includes('-') ? identifier : null
    }
    : null;
};

export const determineActiveFieldFromQueryParams = ({
  query = defaultQuery
}) => {
  let identifier;
  if(isPluginMode()) {
    identifier = getSearchParamOnPluginURL('field-id');
    return convertUnknownIdentifier({ identifier });
  }
  const queryParams = queryString.parse(query);
  identifier = queryParams['field-id'];
  return convertUnknownIdentifier({ identifier });
};

export const determineActiveFieldFromHashRoute = ({ hash = defaultHash }) => {
  const hashParams = routeParse(hash);
  const identifier = isPluginMode() ? hashParams?.field?.split('?')[0] : hashParams.field;

  return convertUnknownIdentifier({ identifier });
};

export const determineActiveFieldFromOperation = ({
  fields,
  operationUuid
}) => {
  // take the first field (asciibetically), of the current operation if we have an operation
  const sortedFields = Object.values(fields.byId).sort((a, b) =>
    asciibeticalCompare(a.name.toUpperCase(), b.name.toUpperCase())
  );

  // Note: if operationUuid is 'all-operations', this still works
  const fieldsInOp = sortedFields.filter(
    (field) => field.operationId === operationUuid
  );

  const id
    = get(fieldsInOp, '[0].id', null) || get(sortedFields, '[0].id', null);
  const info = extractFieldIdentifier({ fields, id });
  const fieldOperation = (info.field && info.field.operationId) || null;
  operationUuid
    = operationUuid === farmerNavigationDefaultOperation ? '' : fieldOperation;

  const result = {
    id,
    uuid: info.uuid || null,
    field: info.field || null,
    operationUuid: operationUuid || null
  };
  return result.uuid || result.id ? result : null;
};

export const determineActiveField = ({
  query = window.location.query,
  hash = window.location.hash,
  fields = { byId: {}, byUuid: {} },
  operationUuid
} = {}) => {
  let identifier
    = determineActiveFieldFromHashRoute({ hash, operationUuid })
    || determineActiveFieldFromQueryParams({ query, operationUuid })
    || determineActiveFieldFromOperation({ fields, operationUuid });

  if (!identifier) {
    return null;
  }

  if (!identifier.id || !identifier.uuid) {
    identifier = extractFieldIdentifier({ fields, ...identifier });
    if (operationUuid) {
      // we were given an operation, but didn't use it yet
      const fieldOperation
        = (identifier.field && identifier.field.operationId) || null;
      identifier.operationUuid
        = operationUuid === farmerNavigationDefaultOperation
          ? ''
          : fieldOperation;
    }
  }

  return identifier;
};

export const determineActiveProductYear = ({
  getState = () => {},
  scripts = {}
} = {}) => {
  const yearParam = queryParam('year');
  if (yearParam) {
    return yearParam;
  }
  const state = getState();
  // interrogate the scripts themselves
  if (!isEmpty(scripts)) {
    return [...getProductYearsForScripts(state, scripts)].pop();
  }

  // Else get the value in state for the active script type
  return getProductYearByScriptType(state, getActiveScriptType(state));
};

export const determineActiveScriptId = ({
  getState = () => {},
  hash = defaultHash,
  scripts = {},
  ignoreRoute = false
} = {}) => {
  if (!ignoreRoute) {
    // first check to see if it's in the route
    let { scriptId } = routeParse(hash);
    if (scriptId) {
      return scriptId;
    }
  }

  // stop here if there are no scripts
  if (isEmpty(scripts)) {
    return null;
  }

  const startingView = getState()
    ? determineStartingView(getState())
    : scriptTypes.FERTILITY;
  const state = getState();
  const productYear = getActiveProductYear(state);

  // loop through the script types (preferring fertility if not navigating directly to a type)
  return [
    startingView,
    [scriptTypes.FERTILITY, scriptTypes.PLANTING].filter(
      (s) => s !== startingView
    )[0]
  ].reduce((activeScriptId, scriptType) => {
    // continue no further, return activeScriptId if already found
    if (activeScriptId) {
      return activeScriptId;
    }
    // only take scripts of the current script type & product year
    let filteredScripts = Object.values(scripts).filter((script) => {
      return (
        getScriptTypeForScript(script) === scriptType
        && script['cropping-year'] === productYear
      );
    });

    // continue no further, return falsy value if no scripts found after filtering
    if (!filteredScripts.length) {
      return null;
    }

    // sort as if for management list based on script type
    switch (scriptType) {
      case scriptTypes.FERTILITY:
        // do some additional sorting/grouping wrt the fertility activity categories
        filteredScripts = getFertilityScriptsSortedForManageScriptList(
          state,
          filteredScripts
        );
        break;
      case scriptTypes.APPLICATION:
        // do some additional sorting/grouping with the application activity categories
        filteredScripts = getApplicationScriptsSortedForManageScriptList(
          state,
          filteredScripts
        );
        break;
      default:
        filteredScripts = getScriptsSortedForManageScriptList(
          state,
          filteredScripts
        );
        break;
    }

    // find the first-occurring primary script or just give up and take the first script
    const activeScript
      = filteredScripts.find((script) => script.primary) || filteredScripts[0];
    if (activeScript) {
      return activeScript.id;
    }
    return null;
  }, undefined);
};
